import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaHandshake, FaLightbulb, FaShieldAlt, FaUsers, FaChartLine, FaGlobe } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const AboutUsPage = () => {
    const values = [
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Integrity',
            description: 'We conduct our business with the highest standards of professional behavior and ethics.',
            gradient: 'from-blue-500 to-indigo-500'
        },
        {
            icon: <FaLightbulb size={24} />,
            title: 'Innovation',
            description: 'We constantly seek newer and better ways to solve our customers challenges.',
            gradient: 'from-amber-400 to-orange-500'
        },
        {
            icon: <FaHandshake size={24} />,
            title: 'Partnership',
            description: 'We believe in building long-term relationships with our clients based on trust and mutual success.',
            gradient: 'from-emerald-400 to-teal-500'
        },
        {
            icon: <FaUsers size={24} />,
            title: 'Customer Focus',
            description: 'Our customers are at the center of everything we do. Your success is our success.',
            gradient: 'from-rose-400 to-pink-500'
        }
    ];

    const stats = [
        { number: 'Nationwide', label: 'Coverage' },
        { number: '24/7', label: 'Support Available' },
        { number: 'Expert', label: 'Technicians' },
        { number: '100%', label: 'Commitment' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-cyan-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">About Us</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Empowering Business Through <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Technology</span>
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                NextGen Limited Company Inc. is a premier provider of nationwide IT infrastructure and telecommunications solutions. We help businesses connect, scale, and thrive in the digital age.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
                                    alt="Our Team"
                                    className="w-full h-auto rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-10 border border-blue-100">
                            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-6">
                                <FaGlobe size={32} />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                To provide comprehensive, reliable, and innovative IT and telecommunications solutions that enable our clients to achieve their business goals while maintaining the highest standards of service delivery.
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-10 border border-indigo-100">
                            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 mb-6">
                                <FaChartLine size={32} />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                To be the preferred nationwide technology partner for businesses, known for our technical expertise, operational excellence, and unwavering commitment to customer satisfaction.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Values</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Drives Us</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our core values guide every interaction and decision we make.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 group">
                                <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${value.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                                <p className="text-gray-600">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <div key={index}>
                                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-blue-200 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to work with us?</h2>
                    <p className="text-xl text-gray-600 mb-8">
                        Let's discuss how we can help upgrade your infrastructure and drive your business forward.
                    </p>
                    <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                        Get in Touch
                        <FaChevronRight size={14} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutUsPage;
